/* src/services/api.js
 * Serviço de API para geração de áudio (genérico e “hora certa”),
 * com suporte a múltiplos providers (edgetts/elevenlabs) e
 * chave anti-duplicação por (tipo, engine, voiceId, texto).
 */

'use strict';

/* =========================================================================
 * BASE URL DINÂMICA
 * ========================================================================= */
export const API_BASE =
  process.env.REACT_APP_API_BASE_URL ||
  'http://localhost:3007';

/* =========================================================================
 * CONFIGURAÇÃO DE VOZ (CACHE EM MEMÓRIA + FALLBACK LOCALSTORAGE)
 * ========================================================================= */
const LS_KEY = 'voice-config'; // opcional: quem salvar no FE usa esta chave

// Estrutura esperada por tipo: { provider:'edgetts'|'elevenlabs', voiceId:'...' }
const DEFAULT_VOICE = { provider: 'edgetts', voiceId: 'pt-BR-AntonioNeural' };

let voiceConfigCache = {
  comercial: { ...DEFAULT_VOICE },
  evento:    { ...DEFAULT_VOICE },
  news:      { ...DEFAULT_VOICE },
  locutor:   { ...DEFAULT_VOICE }
};

function safeParse(json) {
  try { return JSON.parse(json); } catch { return null; }
}

export function setVoiceConfig(newConfig = {}) {
  // Aceita formato plano: { comercial:{...}, evento:{...}, news:{...}, locutor:{...} }
  voiceConfigCache = {
    comercial: { ...DEFAULT_VOICE, ...(newConfig.comercial || {}) },
    evento:    { ...DEFAULT_VOICE, ...(newConfig.evento    || {}) },
    news:      { ...DEFAULT_VOICE, ...(newConfig.news      || {}) },
    locutor:   { ...DEFAULT_VOICE, ...(newConfig.locutor   || {}) }
  };
}

export async function loadVoiceConfig() {
  // Fonte canônica: GET /api/voice-config
  const res = await fetch(`${API_BASE}/api/voice-config`);
  const data = await res.json().catch(() => ({}));
  if (res.ok && data && (data.success === true || data.comercial || data.locutor)) {
    // Aceita payloads { success:true, config:{...} } ou { comercial:{...}, ... }
    const cfg = data.config || data;
    setVoiceConfig(cfg);
    try { localStorage.setItem(LS_KEY, JSON.stringify(cfg)); } catch {}
    return voiceConfigCache;
  }
  // Fallback: tentar localStorage
  const ls = typeof localStorage !== 'undefined' ? safeParse(localStorage.getItem(LS_KEY)) : null;
  if (ls) setVoiceConfig(ls);
  return voiceConfigCache;
}

export function getVoiceConfig(tipo) {
  const cfg = voiceConfigCache[tipo];
  if (cfg) return cfg;
  // Fallback final: LS
  const ls = typeof localStorage !== 'undefined' ? safeParse(localStorage.getItem(LS_KEY)) : null;
  if (ls && ls[tipo]) return ls[tipo];
  return { ...DEFAULT_VOICE };
}

/* =========================================================================
 * HELPERS
 * ========================================================================= */
function normalizeUrl(urlLike) {
  if (!urlLike) return urlLike;
  if (/^https?:\/\//i.test(urlLike)) return urlLike;
  const base = API_BASE.replace(/\/+$/, '');
  const path = String(urlLike).startsWith('/') ? urlLike : `/${urlLike}`;
  return `${base}${path}`;
}

function pickVoice(params = {}, tipo) {
  // Prioriza parâmetros explícitos; senão, pega do cache por tipo
  const cfg = getVoiceConfig(tipo);
  const engine =
    params.voiceEngine ||
    params.provider ||
    params.engine ||
    cfg.provider ||
    DEFAULT_VOICE.provider;

  const voiceId =
    params.voiceId ||
    params.voice ||
    cfg.voiceId ||
    DEFAULT_VOICE.voiceId;

  return { voiceEngine: engine, voiceId };
}

function buildRequestKey({ tipo, voiceEngine, voiceId, text }) {
  const short = (text || '').slice(0, 50);
  return `${tipo}::${voiceEngine}::${voiceId || 'default'}::${short}`;
}

/* =========================================================================
 * ANTI-DUPLICAÇÃO (POR REQUISIÇÃO EQUIVALENTE EM ANDAMENTO)
 * ========================================================================= */
const activeRequests = new Map(); // key -> Promise

/* =========================================================================
 * GERAÇÃO GENÉRICA /api/generate-audio
 * ========================================================================= */
export async function generateAudio({
  text,
  tipo = 'locutor',
  voiceEngine,
  voiceId
}) {
  if (!text || typeof text !== 'string') {
    throw new Error('Texto inválido para geração de áudio.');
  }

  // Se voz/engine não vieram, pegamos do cache por tipo
  const picked = pickVoice({ voiceEngine, voiceId }, tipo);
  const key = buildRequestKey({ tipo, ...picked, text });

  if (activeRequests.has(key)) {
    return activeRequests.get(key);
  }

  const body = {
    text,
    tipo,
    voiceEngine: picked.voiceEngine, // ex.: 'edgetts' | 'elevenlabs'
    voiceId: picked.voiceId          // ex.: 'pt-BR-AntonioNeural' | 'Rachel'
  };

  const controller = new AbortController();

  const prom = fetch(`${API_BASE}/api/generate-audio`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal: controller.signal
  })
    .then(async (r) => {
      const data = await r.json().catch(() => ({}));
      if (!r.ok || data.success === false) {
        const msg = data.error || 'Erro ao gerar áudio';
        throw new Error(msg);
      }
      const audioUrl = normalizeUrl(data.audioUrl || data.previewPath || null);
      return {
        success: true,
        audioUrl,
        audioRef: data.audioRef || data.fileName || null,
        fileName: data.fileName || data.audioRef || null,
        tipo: data.tipo || tipo,
        voiceName: data.voiceName || null,
        mixed: !!data.mixed,
        duration: typeof data.duration === 'number' ? data.duration : null
      };
    })
    .finally(() => activeRequests.delete(key));

  activeRequests.set(key, prom);
  return prom;
}

/* Wrappers por tipo (compatibilidade e conveniência) */
export const generateComercial = (args) => generateAudio({ ...args, tipo: 'comercial' });
export const generateEvento    = (args) => generateAudio({ ...args, tipo: 'evento'    });
export const generateNews      = (args) => generateAudio({ ...args, tipo: 'news'      });
export const generateLocutor   = (args) => generateAudio({ ...args, tipo: 'locutor'   });

/* =========================================================================
 * “HORA CERTA” /api/locutor/falar-cache  (usa timestamp ajustado)
 * ========================================================================= */
export async function gerarHoraCerta({
  nomeComercio,
  timestampISO, // ex.: timeManager.getAdjustedTime().toISOString()
  periodo,      // ex.: timeManager.getPeriod()
  diaSemana,    // ex.: timeManager.getDayOfWeek()
  origem = 'auto',
  voiceEngine,
  voiceId
}) {
  const picked = pickVoice({ voiceEngine, voiceId }, 'locutor');

  const payload = {
    nomeComercio,
    origem,
    timestamp: timestampISO,
    periodo,
    diaSemana,
    voiceEngine: picked.voiceEngine,
    voiceId: picked.voiceId
  };

  const res = await fetch(`${API_BASE}/api/locutor/falar-cache`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.success === false) {
    const msg = data.error || 'Erro ao gerar hora certa';
    throw new Error(msg);
  }

  return {
    success: true,
    audioUrl: normalizeUrl(data.audioUrl),
    fala: data.fala || null,
    duration: typeof data.duration === 'number' ? data.duration : null,
    fromCache: !!data.fromCache
  };
}

/* =========================================================================
 * DEBUG/UTILS
 * ========================================================================= */
export function debugVoiceConfig() {
  const tipos = ['comercial', 'evento', 'news', 'locutor'];
  const out = {};
  tipos.forEach((tipo) => {
    const cfg = getVoiceConfig(tipo) || {};
    out[tipo] = {
      provider: cfg.provider || cfg.engine || 'edgetts',
      voiceId: cfg.voiceId || cfg.voice || 'default'
    };
  });
  // Log enxuto
  try { console.log('[api] voiceConfig:', out); } catch {}
  return out;
}

/* =========================================================================
 * EXPORT DEFAULT (FACILITA IMPORTAR COMO api.*)
 * ========================================================================= */
const api = {
  API_BASE,
  // config
  loadVoiceConfig,
  setVoiceConfig,
  getVoiceConfig,
  debugVoiceConfig,
  // geração genérica + atalhos
  generateAudio,
  generateComercial,
  generateEvento,
  generateNews,
  generateLocutor,
  // hora certa
  gerarHoraCerta
};

export default api;
